import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useGoogleMapsLoader } from "../../hooks/useGoogleMapsLoader";
import { GoogleMap, DrawingManager } from "@react-google-maps/api";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthProvider";
import { createCommunity } from "../../services/community.service";
import { Building, MapPin, Search } from "lucide-react"; // Icons for better UI

type CommunityOnboardingProps = {
  onSaved?: () => void;
  onClose?: () => void;
};

// --- CONFIGURATION ---
const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "100%",
  borderRadius: "0.75rem",
};
const DEFAULT_CENTER = {
  lat: 12.9716, // Bangalore
  lng: 77.5946,
};

// --- TYPES ---
interface CommunityFormData {
  name: string;
  shortId: string;
  blockType: string;
  blockNames: string[];
  totalFloors: number;
  hasGroundFloor: boolean;
  groundFloorLabel: string;
  flatsPerFloor: number;
  flatNumberPadding: number;
  geoFenceCoords: { lat: number; lng: number }[];
}

const CommunityOnboarding: React.FC<CommunityOnboardingProps> = ({
  onSaved,
  onClose,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);

  // Form State
  const [formData, setFormData] = useState<CommunityFormData>({
    name: "",
    shortId: "",
    blockType: "Block",
    blockNames: [],
    totalFloors: 0,
    hasGroundFloor: true,
    groundFloorLabel: "G",
    flatsPerFloor: 0,
    flatNumberPadding: 2,
    geoFenceCoords: [],
  });
  const [previewAddress, setPreviewAddress] = useState("");

  // Search State
  const [searchText, setSearchText] = useState("");
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Maps Refs
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  const { isLoaded, loadError } = useGoogleMapsLoader();

  // --- LOGIC ---
  const generateBlocks = (count: number) => {
    if (count <= 0 || count > 26) return toast.error("Block count must be 1-26");
    const blocks: string[] = [];
    for (let i = 0; i < count; i++) blocks.push(String.fromCharCode(65 + i)); // A, B, C...
    setFormData((prev) => ({ ...prev, blockNames: blocks }));
  };

  // Live Preview
  useEffect(() => {
    if (formData.blockNames.length > 0 && formData.totalFloors > 0 && formData.flatsPerFloor > 0) {
      const sampleBlock = formData.blockNames[0];
      const flatNum = `${formData.hasGroundFloor ? '1' : '10'}01`;
      setPreviewAddress(`${formData.blockType} ${sampleBlock}, Unit ${flatNum}, ${formData.name}`);
    } else {
      setPreviewAddress("Fill details to see preview...");
    }
  }, [formData]);

  // --- MAP HANDLERS ---
  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    // Initialize Services once map is ready
    if (window.google && window.google.maps) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(map);
    }
  };

  const onPolygonComplete = (polygon: google.maps.Polygon) => {
    if (polygonRef.current) polygonRef.current.setMap(null); // Remove old
    polygonRef.current = polygon;
    
    updatePolygonCoords(polygon); // Save initial
    toast.success("Geofence Captured!");

    // Enable editing
    polygon.setEditable(true);
    polygon.setDraggable(true);

    // Listen for edits
    const path = polygon.getPath();
    ['set_at', 'insert_at', 'remove_at'].forEach(evt => {
        window.google.maps.event.addListener(path, evt, () => updatePolygonCoords(polygon));
    });
  };

  const updatePolygonCoords = (polygon: google.maps.Polygon) => {
    const path = polygon.getPath();
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      coords.push({ lat: path.getAt(i).lat(), lng: path.getAt(i).lng() });
    }
    setFormData((prev) => ({ ...prev, geoFenceCoords: coords }));
  };

  const drawingManagerOptions = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    return {
      drawingControl: true,
      drawingControlOptions: {
        position: window.google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        fillColor: "#EA580C",
        fillOpacity: 0.3,
        strokeWeight: 2,
        strokeColor: "#C2410C",
        clickable: true,
        editable: true,
        zIndex: 1,
      },
    };
  }, [isLoaded]);

  // --- ✅ FIXED: NATIVE SEARCH LOGIC ---
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchText(val);
    
    if (!val.trim() || !autocompleteService.current) {
        setPredictions([]);
        return;
    }

    setIsSearching(true);
    
    // Call Native Google API
    autocompleteService.current.getPlacePredictions({
        input: val,
        componentRestrictions: { country: 'in' }, // Limit to India
        locationBias: { 
            radius: 50000, // 50km bias
            center: mapCenter 
        } 
    }, (results, status) => {
        setIsSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            setPredictions(results);
        } else {
            setPredictions([]);
        }
    });
  };

  const handleSelectPrediction = (placeId: string, description: string) => {
    setSearchText(description);
    setPredictions([]);

    if (!placesService.current) return;

    // Get Lat/Lng details
    placesService.current.getDetails({
        placeId: placeId,
        fields: ['geometry', 'formatted_address']
    }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
            const loc = place.geometry.location;
            const newCenter = { lat: loc.lat(), lng: loc.lng() };
            
            setMapCenter(newCenter);
            mapRef.current?.panTo(newCenter);
            mapRef.current?.setZoom(17); // Zoom in closer
        } else {
            toast.error("Could not fetch location details");
        }
    });
  };

  // --- SAVE HANDLER (Same logic, shortened for clarity) ---
  const handleSave = async () => {
    if (!formData.name || !formData.shortId) return toast.error("Name & Short ID required");
    if (formData.geoFenceCoords.length < 3) return toast.error("Draw a geofence first");
    if (!user) return toast.error("Auth required");

    setLoading(true);
    try {
      // 1. Generate Structural Arrays
      const floorValues = [];
      if (formData.hasGroundFloor) floorValues.push(formData.groundFloorLabel);
      for (let i = 1; i <= formData.totalFloors; i++) floorValues.push(String(i));

      const flatValues = [];
      for (let i = 1; i <= formData.flatsPerFloor; i++) flatValues.push(String(i).padStart(formData.flatNumberPadding, "0"));

      // 2. Build Payload
      const payload = {
        name: formData.name.trim(),
        shortId: formData.shortId.toUpperCase(),
        area: "Bangalore", // Ideally get from geocoder
        city: "Bangalore",
        pincode: "560001",
        servingRadius: 5,
        centerLat: formData.geoFenceCoords[0].lat,
        centerLng: formData.geoFenceCoords[0].lng,
        address: `${formData.name}, Bangalore`,
        status: "active",
        level1: { id: "l1", label: formData.blockType, values: formData.blockNames },
        level2: { id: "l2", label: "Floor", values: floorValues },
        level3: { id: "l3", label: "Unit", prefixLevel1: true, values: flatValues },
        geoFenceCoords: formData.geoFenceCoords,
      };

      await createCommunity(payload, user.uid);
      toast.success("Community Onboarded Successfully!");
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  if (loadError) return <div className="p-10 text-red-500">Map Error: Check API Key</div>;
  if (!isLoaded) return <div className="p-10 text-gray-500">Loading Maps...</div>;

  return (
    <div className="p-6 bg-white shadow-xl rounded-xl border border-gray-100 max-w-6xl mx-auto animate-in fade-in">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Building className="text-orange-600"/> Onboard Community
        </h1>
        <div className={`text-xs px-3 py-1 rounded-full font-bold ${formData.geoFenceCoords.length > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
          {formData.geoFenceCoords.length > 0 ? "✅ Geofence Ready" : "⭕ Draw Geofence Required"}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left: Inputs */}
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
             <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Name *</label>
                <input className="w-full border p-2 rounded focus:border-orange-500 outline-none" 
                  value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Sobha City"/>
             </div>
             <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Short ID *</label>
                <input className="w-full border p-2 rounded focus:border-orange-500 outline-none uppercase" 
                  value={formData.shortId} onChange={e => setFormData({...formData, shortId: e.target.value.toUpperCase()})} placeholder="e.g. SC"/>
             </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg text-xs text-blue-800 border border-blue-100">
             <strong>📍 Instructions:</strong>
             <ol className="list-decimal ml-4 mt-1 space-y-1">
                <li>Use the search bar on the map to find the society.</li>
                <li>Select the <strong>Polygon Tool (📐)</strong> from the top-center menu.</li>
                <li>Click points to draw the boundary. Double-click to finish.</li>
             </ol>
          </div>
        </div>

        {/* Right: Map */}
        <div className="h-80 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-300 relative">
           <GoogleMap mapContainerStyle={MAP_CONTAINER_STYLE} center={mapCenter} zoom={15} onLoad={onMapLoad} options={{ mapTypeControl: false, streetViewControl: false }}>
              
              {/* SEARCH BAR OVERLAY */}
              <div className="absolute top-2 left-2 right-14 z-10">
                 <div className="relative">
                    <input 
                      type="text" 
                      placeholder="Search location..." 
                      className="w-full pl-10 pr-4 py-2.5 rounded shadow-lg border-0 outline-none text-sm font-medium"
                      value={searchText}
                      onChange={handleSearchChange}
                    />
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={16}/>
                    {isSearching && <div className="absolute right-3 top-2.5 w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"/>}
                 </div>

                 {/* DROPDOWN RESULTS */}
                 {predictions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-100 max-h-60 overflow-y-auto">
                        {predictions.map(p => (
                            <div key={p.place_id} 
                                 onClick={() => handleSelectPrediction(p.place_id, p.description)}
                                 className="px-4 py-2 hover:bg-orange-50 cursor-pointer border-b border-gray-50 last:border-0 text-sm">
                                <p className="font-medium text-gray-800 truncate">{p.structured_formatting.main_text}</p>
                                <p className="text-xs text-gray-500 truncate">{p.structured_formatting.secondary_text}</p>
                            </div>
                        ))}
                    </div>
                 )}
              </div>

              {drawingManagerOptions && <DrawingManager onPolygonComplete={onPolygonComplete} options={drawingManagerOptions}/>}
           </GoogleMap>
        </div>
      </div>

      <hr className="my-6 border-gray-200"/>

      {/* Structure Definition */}
      <h2 className="text-lg font-bold text-gray-800 mb-4">🏢 Structure Definition</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
         
         {/* Blocks Config */}
         <div className="border p-4 rounded-xl bg-gray-50">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-3">Verticals</h3>
            <div className="flex gap-2 mb-3">
               <select className="border p-2 rounded text-sm w-1/3" value={formData.blockType} onChange={e => setFormData({...formData, blockType: e.target.value})}>
                  <option>Block</option><option>Tower</option><option>Wing</option>
               </select>
               <input type="number" placeholder="Count" className="border p-2 rounded text-sm w-2/3" min="1" max="26"
                  onChange={e => {
                      const val = parseInt(e.target.value);
                      if(!isNaN(val)) generateBlocks(val);
                  }}
               />
            </div>
            <div className="text-xs text-gray-500 bg-white p-2 rounded border h-16 overflow-y-auto font-mono">
               {formData.blockNames.join(", ") || "A, B, C..."}
            </div>
         </div>

         {/* Unit Config */}
         <div className="border p-4 rounded-xl bg-gray-50">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-3">Units</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
               <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Total Floors</label>
                  <input type="number" className="w-full border p-2 rounded text-sm" placeholder="e.g. 10"
                     value={formData.totalFloors || ""} onChange={e => setFormData({...formData, totalFloors: parseInt(e.target.value)||0})}/>
               </div>
               <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase">Flats/Floor</label>
                  <input type="number" className="w-full border p-2 rounded text-sm" placeholder="e.g. 4"
                     value={formData.flatsPerFloor || ""} onChange={e => setFormData({...formData, flatsPerFloor: parseInt(e.target.value)||0})}/>
               </div>
            </div>
            <div className="flex items-center gap-2">
               <input type="checkbox" checked={formData.hasGroundFloor} onChange={e => setFormData({...formData, hasGroundFloor: e.target.checked})}/>
               <span className="text-xs text-gray-600">Has Ground Floor (G01)</span>
            </div>
         </div>
      </div>

      {/* Footer */}
      <div className="mt-8 flex items-center justify-between bg-orange-50 p-4 rounded-xl border border-orange-200">
         <div>
            <p className="text-xs font-bold text-orange-800 uppercase">Preview Address</p>
            <p className="text-lg font-bold text-orange-900 font-mono">{previewAddress}</p>
         </div>
         <button 
            onClick={handleSave} 
            disabled={loading}
            className="bg-orange-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-orange-700 shadow-lg disabled:opacity-50 transition-all"
         >
            {loading ? "Saving..." : "SAVE COMMUNITY"}
         </button>
      </div>
    </div>
  );
};

export default CommunityOnboarding;